/**
 * Basic Authentication utilities for the client
 * Stores credentials in sessionStorage and provides functions to prompt for credentials
 */

const AUTH_USERNAME_KEY = 'itemize_auth_username'
const AUTH_PASSWORD_KEY = 'itemize_auth_password'

/**
 * Get stored Basic Auth credentials from sessionStorage
 */
export const getStoredCredentials = (): { username: string; password: string } | null => {
  const username = sessionStorage.getItem(AUTH_USERNAME_KEY)
  const password = sessionStorage.getItem(AUTH_PASSWORD_KEY)

  if (username && password) {
    return { username, password }
  }

  return null
}

/**
 * Store Basic Auth credentials in sessionStorage
 */
export const storeCredentials = (username: string, password: string): void => {
  sessionStorage.setItem(AUTH_USERNAME_KEY, username)
  sessionStorage.setItem(AUTH_PASSWORD_KEY, password)
}

/**
 * Clear stored Basic Auth credentials
 */
export const clearCredentials = (): void => {
  sessionStorage.removeItem(AUTH_USERNAME_KEY)
  sessionStorage.removeItem(AUTH_PASSWORD_KEY)
}

/**
 * Prompt user for Basic Auth credentials
 * Returns null if user cancels
 */
export const promptForCredentials = (): { username: string; password: string } | null => {
  const username = prompt('Enter username:')
  if (username === null) {
    return null
  }

  const password = prompt('Enter password:')
  if (password === null) {
    return null
  }

  return { username, password }
}

/**
 * Get or prompt for Basic Auth credentials
 * First checks sessionStorage, then prompts if not found
 */
export const getOrPromptCredentials = (): { username: string; password: string } | null => {
  const stored = getStoredCredentials()
  if (stored) {
    return stored
  }

  const credentials = promptForCredentials()
  if (credentials) {
    storeCredentials(credentials.username, credentials.password)
    return credentials
  }

  return null
}

/**
 * Create Basic Auth header value from credentials
 */
export const createBasicAuthHeader = (username: string, password: string): string => {
  const credentials = `${username}:${password}`
  return `Basic ${btoa(credentials)}`
}

/**
 * Get Basic Auth header value, prompting if needed
 */
export const getBasicAuthHeader = (): string | null => {
  const credentials = getOrPromptCredentials()
  if (!credentials) {
    return null
  }

  return createBasicAuthHeader(credentials.username, credentials.password)
}
